<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import FullnameIncrementalField from '@/components/incremental-data/fields/fullname-incremental-field';
import GenderIncrementalField from '@/components/incremental-data/fields/gender-incremental-field';
import LocationIncrementalField from '@/components/incremental-data/fields/location-incremental-field';
import SubscriptionsIncrementalField from '@/components/incremental-data/fields/subscriptions-incremental-field';
import InstitutionStartYearIncrementalField from '@/components/incremental-data/fields/institution-start-year-incremental-field';
import JobTypesIncrementalField from '@/components/incremental-data/fields/job-types-incremental-field';
import JobRolesIncrementalField from '@/components/incremental-data/fields/job-roles-incremental-field.vue';
import EthnicityIncremntalField from '@/components/incremental-data/fields/ethnicity-incremental-field';
import FreeSchoolMealsIncrementalField from '@/components/incremental-data/fields/school-meals-incremental-field';
import ParentProfessionIncremntalField from '@/components/incremental-data/fields/parent-profession-incremental-field';
import CompleteIncrementalStage from '@/components/incremental-data/partials/complete-incremental-stage';
import EventBus from '@/event-bus';

export default {
  data() {
    return {
      ui: {
        loading: false,
        showComponentOnLoad: true,
        animating: false,
      },
      form: {
        success: false,
        error: false,
        validateAll: false,
        serverValidationErrors: {},
      },
    };
  },
  computed: {
    ...mapState('user', {
      user: (state) => state,
    }),
    ...mapGetters('user', {
      getRegistrationQuestions: 'getRegistrationQuestions',
    }),

    questions() {
      const questions = this.getRegistrationQuestions.filter((question) => {
        return question.name !== 'uk_based'
        && question.name !== 'postcode';
      });

      questions.splice(2, 0, {
        name: 'location',
        order: 3,
        valid: this.user.uk_based === 0 || (this.user.uk_based === 1 && this.user.postcode !== null),
      });

      return questions;
    },

    incompleteQuestions() {
      return this.questions.filter((question) => {
        return !question.valid;
      }).map((question) => question.name);
    },

    steps() {
      return this.questions.map((question) => {
        return {
          id: question.order,
          isComplete: question.valid,
          isActive: question.name === this.currentQuestion.name,
        };
      });
    },

    currentQuestion() {
      // if all questions are complete, return completion stage.
      if (this.incompleteQuestions.length === 0) {
        return {
          component: CompleteIncrementalStage,
          name: 'complete',
          index: 0,
        };
      }

      const currentQuestionName = this.incompleteQuestions[0];

      const index = this.questions.findIndex((question) => {
        return question.name === currentQuestionName;
      }) + 1;

      return {
        component: this.resolveQuestionNameToComponent(currentQuestionName),
        name: currentQuestionName,
        index,
      };
    },

    hasQuestionsToAnswer() {
      return this.currentQuestion.index !== 0;
    },
  },

  mounted() {
    if (this.user.profile_completion === 100) {
      this.ui.showComponentOnLoad = false;
    }
  },

  methods: {
    ...mapActions('user', {
      storeUpdateUser: 'updateUser',
    }),

    updateUser({ payload, fieldName }) {
      if (this.ui.loading) {
        return false;
      }

      this.form.validateAll = true;

      this.form.serverValidationErrors = {};

      this.ui.loading = true;

      return this.storeUpdateUser(payload)
        .then(() => {
          this.$gtag.send({
            event: 'update-success',
            action: fieldName,
          });

          this.ui.loading = false;

          this.form.validateAll = false;
        })
        .catch((error) => {
          this.$gtag.send({
            event: 'update-fail',
            action: fieldName,
          });

          // Update states.
          this.ui.loading = false;
          this.form.error = true;

          this.form.serverValidationErrors = error.response.data.meta
            .reduce((accumulator, errors) => {
              accumulator[errors.field] = errors.message;
              return accumulator;
            }, {});
        });
    },

    setAnimationStatus(animationToggle) {
      if (animationToggle) {
        this.ui.animating = animationToggle;
      } else {
        window.setTimeout(() => {
          this.ui.animating = animationToggle;
        }, 140);
      }
    },

    resolveQuestionNameToComponent(questionName) {
      switch (questionName) {
        case 'fullname':
          return FullnameIncrementalField;
        case 'gender':
          return GenderIncrementalField;
        case 'institution_start_year':
          return InstitutionStartYearIncrementalField;
        case 'location':
          return LocationIncrementalField;
        case 'subscriptions':
          return SubscriptionsIncrementalField;
        case 'job_types':
          return JobTypesIncrementalField;
        case 'job_roles':
          return JobRolesIncrementalField;
        case 'ethnicity':
          return EthnicityIncremntalField;
        case 'free_school_meals':
          return FreeSchoolMealsIncrementalField;
        case 'parent_profession':
          return ParentProfessionIncremntalField;
        default:
          return CompleteIncrementalStage;
      }
    },

    showValidationErrors() {
      this.form.validateAll = true;
    },
  },

  render() {
    const slot = this.$slots.default({
      props: {
        ui: this.ui,
        form: this.form,
        hasQuestionsToAnswer: this.hasQuestionsToAnswer,
        steps: this.steps,
        currentQuestion: this.currentQuestion,
        user: this.user,
      },
      listeners: {
        setAnimationStatus: this.setAnimationStatus,
        updateUser: this.updateUser,
        showValidationErrors: this.showValidationErrors,
      },
    });

    return Array.isArray(slot) ? slot[0] : slot;
  },
};
</script>
